import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect } from "react"
import { Controller, useForm, useWatch } from "react-hook-form"
import { useShallow } from "zustand/react/shallow"

import { Button } from "@/components/ui/button"
import { Checkbox, CheckboxGroup } from "@/components/ui/checkbox"
import { HintText, Label } from "@/components/ui/input"
import { RadioButton, RadioGroup } from "@/components/ui/radio-buttons"
import { SectionFooter } from "@/components/ui/section-footer"
import { SectionHeader } from "@/components/ui/section-header"
import { SectionLabel } from "@/components/ui/section-label"

import { useFormTabsStore } from "../../components"
import { useCheckinForm } from "../../stores/useCheckinFormStore"
import { CALDMultiselect } from "../fields"

import { demographicsSchema } from "./demographics.schema"

interface IDemographics {
  readOnly?: boolean
}

const Demographics: React.FC<IDemographics> = ({ readOnly = false }) => {
  const defaultData = useCheckinForm(
    useShallow((s) => s.getDefaultValues(demographicsSchema.keyof().options)),
  )

  const updateForm = useCheckinForm((s) => s.updateForm)

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitted },
    subscribe,
  } = useForm({
    resolver: readOnly ? undefined : zodResolver(demographicsSchema),
    mode: "onSubmit",
    reValidateMode: "onBlur",
    defaultValues: {
      critical_incident_occurred:
        defaultData.critical_incident_occurred || false,
      critical_incidents: defaultData.critical_incidents || [],
      cald_backgrounds: defaultData.cald_backgrounds || [],
    },
    disabled: readOnly,
  })
  const hasHadCriticalIncidents = useWatch({
    control,
    name: "critical_incident_occurred",
  })

  const onNext = useFormTabsStore((s) => s.onNext)
  const handleBack = useFormTabsStore((s) => s.onBack)
  const setTabState = useFormTabsStore((s) => s.setStatus)

  useEffect(() => {
    // make sure to unsubscribe;
    const callback = subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        updateForm(values)
      },
    })

    return () => callback()
  }, [subscribe, updateForm])

  const handleNext = handleSubmit(
    (data) => {
      setTabState("complete")
      updateForm(data)
      onNext()
    },
    () => {
      setTabState("error")
    },
  )

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
            <SectionHeader.Heading>Cohort Demographics</SectionHeader.Heading>
            <SectionHeader.Subheading>
              Help us understand the student cohort
            </SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root
            size="sm"
            title="Critical Incidents"
            description="In the past 6 months"
            className="max-lg:hidden"
          />
          <div>
            <Controller
              control={control}
              name="critical_incident_occurred"
              render={({ field: { value, onChange, onBlur, disabled } }) => (
                <>
                  <Label className="lg:hidden">
                    Has there been a critical incident at the school in the past
                    6 months
                  </Label>
                  <RadioGroup
                    aria-label="Has there been a critical incident at the school in the past 6 months"
                    className="flex flex-row gap-4 py-4"
                    value={value ? "yes" : "no"}
                    onChange={(value: string) => onChange(value === "yes")}
                    onBlur={onBlur}
                    isDisabled={disabled}
                    isRequired
                  >
                    <RadioButton label="Yes" value="yes" />
                    <RadioButton label="No" value="no" />
                  </RadioGroup>
                </>
              )}
            />
            {hasHadCriticalIncidents && (
              <Controller
                control={control}
                name="critical_incidents"
                render={({
                  field: { value, onChange, onBlur, disabled },
                  fieldState: { error },
                }) => (
                  <CheckboxGroup
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    isDisabled={disabled}
                    isRequired
                    className={
                      error ? "ring-error rounded-lg p-3 ring-1 ring-inset" : ""
                    }
                  >
                    <div>
                      <Label>Tick any that apply</Label>
                      <HintText>
                        This is core to trauma-informed session design and
                        determines safety tone and support planning
                      </HintText>
                    </div>
                    <div className="pt-4">
                      <Label>Bereavement / Loss</Label>
                      <div className="px-6 py-2">
                        <Checkbox
                          value="death-current"
                          label="Death of a current student or staff member"
                          size="sm"
                        />
                        <Checkbox
                          value="death-former"
                          label="Death of a former student closely connected to current students"
                          size="sm"
                        />
                        <Checkbox
                          value="death-suicide"
                          label="Death by suicide (suspected or confirmed) affecting the school community"
                          size="sm"
                        />
                      </div>
                      <Label>Suicide and self-harm related pressures</Label>
                      <div className="px-6 py-2">
                        <Checkbox
                          value="suicide-attempt"
                          label="Recent suicide attempt(s) that required emergency/clinical response (community impact)"
                          size="sm"
                        />
                        <Checkbox
                          value="self-harm-cluster"
                          label="Noticeable cluster of self-harm presentations or disclosures"
                          size="sm"
                        />
                        <Checkbox
                          value="self-harm-discussion"
                          label="Ongoing intense social media discussion around self-harm or death"
                          size="sm"
                        />
                      </div>
                    </div>
                    {error && <HintText isInvalid>{error.message}</HintText>}
                  </CheckboxGroup>
                )}
              />
            )}
          </div>
        </div>

        <hr className="bg-border-secondary h-px w-full border-none" />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
          <SectionLabel.Root
            size="sm"
            title="Cultural Diversity"
            className="max-lg:hidden"
          />
          <div>
            <Controller
              control={control}
              name="cald_backgrounds"
              render={({ field: { value, onChange, disabled } }) => (
                <CALDMultiselect
                  value={value}
                  onChange={onChange}
                  isDisabled={disabled}
                />
              )}
            />
          </div>
        </div>
      </div>
      <SectionFooter.Root>
        <SectionFooter.Actions>
          <Button color="secondary" onPress={handleBack}>
            Back
          </Button>
          <Button
            isDisabled={isSubmitted && !isValid}
            color="primary"
            onClick={handleNext}
          >
            Next
          </Button>
        </SectionFooter.Actions>
      </SectionFooter.Root>
    </div>
  )
}

export default Demographics
